/* Tidal streams: how hard it is running, which way, and when it stops.
 *
 * This is the thing no consumer plotter puts on a chart. Raymarine and Garmin
 * will sell you tide heights all day and leave you to guess whether Dodd
 * Narrows is going to be 5 knots against you at the moment you get there.
 *
 * ═══ THE TWO CONVENTIONS, WHICH ARE NOT THE SAME ═══
 *
 * The source dataset uses BOTH, and mixing them turns every ebb into a flood:
 *
 *   the 10-minute SERIES is SIGNED     — negative means ebb
 *   the EVENTS are UNSIGNED MAGNITUDE  — the direction is in the `kind` word
 *
 * We ship the events, so everything here starts from magnitude-plus-kind and
 * signs it exactly once, in `signed()`. Flood is positive throughout, matching
 * CHS's own sense.
 *
 * ═══ WHY A COSINE IS ENOUGH ═══
 *
 * Only the slack and maximum events are shipped — a hundredth of the size of
 * the full series (see ops/build-currents.mjs). Between a slack and the
 * following maximum a tidal stream is very close to a half-cosine, which is the
 * same approximation `$lib/tidegauge` already makes between high and low water
 * and for the same reason: it is right where it matters and it needs four
 * numbers instead of fifty thousand.
 *
 * ═══ PROVENANCE ═══
 *
 * Event times and rates were checked against tides.gc.ca before any of this was
 * written — Active Pass, Dodd Narrows and Seymour Narrows, all matching CHS to
 * three decimals. The DIRECTIONS are ours: CHS publishes a direction series but
 * the shipped dataset drops it, so per-station flood/ebb axes were derived once
 * from `wcdp1` (ops/derive-current-axes.mjs). A station whose stream does not
 * reverse along an axis carries no bearing at all rather than a plausible
 * guess.
 */

import type { Gate } from './passage.ts';
import type { RouteWarning } from './router.ts';

export type Phase = 'SLACK' | 'FLOOD' | 'EBB';

export interface CurrentEvent {
  /** Epoch ms. */
  t: number;
  kind: Phase;
  /** Knots, unsigned — the direction is the kind. */
  kt: number;
}

export interface CurrentStation {
  code: string;
  name: string;
  lat: number;
  lon: number;
  /** Degrees true the stream sets on the flood, or null if it has no axis. */
  flood: number | null;
  ebb: number | null;
  axisConfidence: number | null;
  /** A CHS Vol-6 subordinate current station — a real named rapids predicted
   * by corrections on a reference station, not a first-class IWLS station.
   * Drawn and labelled with less authority. */
  derived?: boolean;
  /** The code of the station it is corrected from, when known. */
  refCode?: string;
  events: CurrentEvent[];
}

/** The packed on-disk shape — see ops/build-currents.mjs. */
export interface PackedCurrents {
  year: number;
  epochUtcMs: number;
  stations: {
    code: string;
    name: string;
    lat: number;
    lon: number;
    flood: number | null;
    ebb: number | null;
    axisConfidence: number | null;
    derived?: boolean;
    /** A derived station may carry NO events of its own: it is its reference
     * station's events plus a Vol-6 correction, and unpack materialises it.
     * Keep this in step with $lib/currentsbuild's Vol6CurrentCorrection. */
    ref?: {
      code: string;
      dtF: number | null;
      dtE: number | null;
      dtS: number | null;
      fF: number;
      eF: number;
      fO: number | null;
      eO: number | null;
    };
    dt: number[];
    kind: string;
    cKt: number[];
  }[];
}

const KIND: Record<string, Phase> = { S: 'SLACK', F: 'FLOOD', E: 'EBB' };

/** One packed station's events, from its delta arrays. */
function decodeEvents(
  s: PackedCurrents['stations'][number],
  epochUtcMs: number
): CurrentEvent[] {
  const events: CurrentEvent[] = [];
  let mins = 0;
  for (let i = 0; i < s.dt.length; i++) {
    mins += s.dt[i]!;
    events.push({
      t: epochUtcMs + mins * 60_000,
      kind: KIND[s.kind[i]!] ?? 'SLACK',
      kt: (s.cKt[i] ?? 0) / 100
    });
  }
  return events;
}

/** Undo the delta/centi-knot packing, materialising any derived station that
 * ships as a reference-plus-correction into the same full event list every
 * other station has. */
export function unpack(p: PackedCurrents): CurrentStation[] {
  const plain = p.stations.map((s) => ({
    station: {
      code: s.code,
      name: s.name,
      lat: s.lat,
      lon: s.lon,
      flood: s.flood,
      ebb: s.ebb,
      axisConfidence: s.axisConfidence,
      derived: s.derived,
      refCode: s.ref?.code,
      events: decodeEvents(s, p.epochUtcMs)
    },
    ref: s.ref
  }));

  const byCode = new Map(plain.map((x) => [x.station.code, x.station]));
  return plain.map(({ station, ref }) => {
    if (ref && station.events.length === 0) {
      const parent = byCode.get(ref.code);
      if (parent) {
        const ev: CurrentEvent[] = [];
        for (const e of parent.events) {
          if (e.kind === 'FLOOD') {
            ev.push({
              t: e.t + (ref.dtF ?? 0) * 60_000,
              kind: e.kind,
              kt: Math.max(0, Math.round((e.kt * ref.fF + (ref.fO ?? 0)) * 1000) / 1000)
            });
          } else if (e.kind === 'EBB') {
            ev.push({
              t: e.t + (ref.dtE ?? 0) * 60_000,
              kind: e.kind,
              kt: Math.max(0, Math.round((e.kt * ref.eF + (ref.eO ?? 0)) * 1000) / 1000)
            });
          } else {
            ev.push({ t: e.t + (ref.dtS ?? 0) * 60_000, kind: e.kind, kt: 0 });
          }
        }
        return { ...station, events: ev.sort((a, b) => a.t - b.t) };
      }
    }
    return station;
  });
}

/** Magnitude and kind to one signed number. Flood positive, as CHS has it. */
export const signed = (e: CurrentEvent): number =>
  e.kind === 'EBB' ? -e.kt : e.kind === 'FLOOD' ? e.kt : 0;

/** Index of the last event at or before `t`, or -1. Binary search. */
function before(events: CurrentEvent[], t: number): number {
  let lo = 0;
  let hi = events.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (events[mid]!.t <= t) {
      ans = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return ans;
}

export interface CurrentNow {
  /** Knots, SIGNED: positive floods, negative ebbs. */
  kt: number;
  /** Which way the stream is going right now. */
  phase: Phase;
  /** Degrees true, or null when the station has no axis. */
  setDeg: number | null;
  /** Epoch ms of the next slack, or null past the end of the data. */
  nextSlack: number | null;
  /** Epoch ms and signed rate of the next maximum, or null. */
  nextMax: { t: number; kt: number } | null;
}

/**
 * The state of the stream at `t`, interpolated between the shipped events.
 *
 * Returns null outside the covered year rather than extrapolating — a
 * prediction past the end of the dataset is a fabrication, and this is a number
 * people time a transit of Seymour Narrows by.
 */
export function currentAt(st: CurrentStation, t: number): CurrentNow | null {
  const ev = st.events;
  if (ev.length < 2) return null;
  const i = before(ev, t);
  if (i < 0 || i >= ev.length - 1) return null;

  const a = ev[i]!;
  const b = ev[i + 1]!;
  const va = signed(a);
  const vb = signed(b);
  const span = b.t - a.t;
  /* Two events at the same instant would divide by zero; take the later one
   * rather than producing NaN knots. */
  const u = span > 0 ? (t - a.t) / span : 1;
  // Half-cosine: flat at both ends, steepest halfway. See the header.
  const kt = va + (vb - va) * ((1 - Math.cos(Math.PI * u)) / 2);

  /* The PHASE is the kind of whichever end is not slack — the stream between a
   * slack and a flood maximum is flooding for the whole of that interval, even
   * while the rate is still near zero. Deriving it from the sign of the
   * interpolated rate instead would report SLACK for the twenty minutes either
   * side of the turn, when what the helm wants to know is which way it is about
   * to run. */
  const phase: Phase = a.kind !== 'SLACK' ? a.kind : b.kind !== 'SLACK' ? b.kind : 'SLACK';

  let nextSlack: number | null = null;
  for (let j = i + 1; j < ev.length; j++) {
    if (ev[j]!.kind === 'SLACK') {
      nextSlack = ev[j]!.t;
      break;
    }
  }
  let nextMax: { t: number; kt: number } | null = null;
  for (let j = i + 1; j < ev.length; j++) {
    if (ev[j]!.kind !== 'SLACK') {
      nextMax = { t: ev[j]!.t, kt: signed(ev[j]!) };
      break;
    }
  }

  return {
    kt,
    phase,
    setDeg: kt > 0 ? st.flood : kt < 0 ? st.ebb : null,
    nextSlack,
    nextMax
  };
}

/** The next event of any of `kinds` strictly after `fromT`, or null when the
 * data has nothing left — e.g. the next SLACK, or the next FLOOD/EBB maximum
 * past a scrubbed instant. Binary-searched like `before()`, so it is a couple
 * of comparisons even mid-year. */
export function nextEventOf(
  st: CurrentStation,
  fromT: number,
  kinds: readonly Phase[]
): CurrentEvent | null {
  const ev = st.events;
  let lo = 0;
  let hi = ev.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ev[mid]!.t <= fromT) lo = mid + 1;
    else hi = mid;
  }
  for (let j = lo; j < ev.length; j++) {
    if (kinds.includes(ev[j]!.kind)) return ev[j]!;
  }
  return null;
}

/** Stations within `km` of a position, nearest first. */
export function near(
  stations: CurrentStation[],
  pos: { lat: number; lon: number },
  km: number
): CurrentStation[] {
  const R = 111.32;
  const cos = Math.cos((pos.lat * Math.PI) / 180);
  return stations
    .map((s) => ({
      s,
      d: Math.hypot((s.lat - pos.lat) * R, (s.lon - pos.lon) * R * cos)
    }))
    .filter((x) => x.d <= km)
    .sort((a, b) => a.d - b.d)
    .map((x) => x.s);
}

/** "1h47", "18m", "—". The readout people actually plan around. */
export function untilText(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, '0')}`;
}

/* ---------- route proximity, M19.2 ---------- */

/**
 * Perpendicular (clamped-to-segment) distance from `p` to a-b, in km — the
 * same small-area equirectangular approximation `near()` uses above, scaled
 * at the segment's own midpoint latitude. Good to well under 1% error over
 * the few-km ranges a current-station proximity check cares about; nobody
 * needs great-circle precision to ask "does this leg pass near Dodd
 * Narrows".
 */
function distanceToSegmentKm(
  p: { lat: number; lon: number },
  a: { lat: number; lon: number },
  b: { lat: number; lon: number }
): number {
  const R = 111.32;
  const cos = Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  const toXY = (q: { lat: number; lon: number }) => ({ x: q.lon * R * cos, y: q.lat * R });
  const A = toXY(a);
  const B = toXY(b);
  const P = toXY(p);
  const abx = B.x - A.x;
  const aby = B.y - A.y;
  const abLen2 = abx * abx + aby * aby;
  const t = abLen2 === 0 ? 0 : Math.max(0, Math.min(1, ((P.x - A.x) * abx + (P.y - A.y) * aby) / abLen2));
  const projX = A.x + t * abx;
  const projY = A.y + t * aby;
  return Math.hypot(P.x - projX, P.y - projY);
}

/** Knots — a route passing near a station that never reaches this is not
 *  worth interrupting the advisory box for. Dodd Narrows (3.5 kt) is
 *  comfortably above it; this sits in the upper half of `rateBand`'s own
 *  "moderate" range rather than reusing "strong" (4 kt), which would miss
 *  it. */
export const HIGH_CURRENT_KT = 3;

/** Kilometres — how close a leg has to pass to count as "near" a station. */
export const HIGH_CURRENT_PROXIMITY_KM = 1;

export interface HighCurrentHazard {
  station: CurrentStation;
  /** The fastest rate the shipped data ever records for this station, kt. */
  peakKt: number;
}

/**
 * Named stations a route's legs pass within `withinKm` of, whose stream
 * reaches at least `minKt` at some point in the shipped data — a first,
 * cheap step toward M19.2 without folding current cost into the router
 * itself (a bigger project the plan defers). The cost grid this route was
 * searched over is depth-only: a route through Dodd Narrows reads exactly
 * as "safe" as one through open water by the grid's own measure, and
 * nothing before this said otherwise. Sorted fastest-first so the worst
 * hazard leads.
 */
export function highCurrentStationsNearRoute(
  pts: readonly { lat: number; lon: number }[],
  stations: readonly CurrentStation[],
  minKt = HIGH_CURRENT_KT,
  withinKm = HIGH_CURRENT_PROXIMITY_KM
): HighCurrentHazard[] {
  const found: HighCurrentHazard[] = [];
  for (const st of stations) {
    let peakKt = 0;
    for (const e of st.events) if (e.kt > peakKt) peakKt = e.kt;
    if (peakKt < minKt) continue;
    for (let i = 0; i < pts.length - 1; i++) {
      if (distanceToSegmentKm(st, pts[i]!, pts[i + 1]!) <= withinKm) {
        found.push({ station: st, peakKt });
        break;
      }
    }
  }
  return found.sort((a, b) => b.peakKt - a.peakKt);
}

/**
 * `hazards` folded into a single `RouteWarning` — never one per station.
 * The advisory box keys warnings by `code` (`ChartPage.svelte`), and every
 * other code this app produces appears at most once per route; a hazard
 * warning follows the same rule instead of colliding on repeat stations.
 */
export function highCurrentRouteWarning(hazards: readonly HighCurrentHazard[]): RouteWarning | null {
  if (hazards.length === 0) return null;
  const names = hazards.map((h) => `${h.station.name} (${h.peakKt.toFixed(1)} kt)`).join(', ');
  const text =
    hazards.length === 1
      ? `Passes near a known strong-current station: ${names}. Check the current before relying on this leg.`
      : `Passes near ${hazards.length} known strong-current stations: ${names}. Check currents before relying on these legs.`;
  return { code: 'high-current', text };
}

/* Colour by how much it matters, not by a smooth ramp. Under a knot you can
 * ignore it, two to four is a real push, and past four you plan the day around
 * it — the thresholds are where a sailing decision changes, which is what a
 * chart symbol is for. */
export function rateBand(kt: number): 'slack' | 'weak' | 'moderate' | 'strong' {
  const a = Math.abs(kt);
  if (a < 0.5) return 'slack';
  if (a < 2) return 'weak';
  if (a < 4) return 'moderate';
  return 'strong';
}

/* ═══ NARROWS AS GATES, FOR THE ROUTER ═══ */

/**
 * How wide a pass is taken to be.
 *
 * The station is a point and the shipped data says nothing about the extent of
 * the water it describes, so this is a judgement, not a measurement. 1.2 km is
 * a little wider than the `HIGH_CURRENT_PROXIMITY_KM` the hazard warning uses,
 * chosen because a route THROUGH a pass passes within a few hundred metres of a
 * station sitting in it — the closest approach is what matters, not the length
 * of the pass — while a route standing off in the strait stays well outside.
 *
 * Too large is worse than too small here: a false gate inserts a wait that is
 * not needed, which makes a route worse for a reason nobody can see.
 */
export const GATE_RADIUS_M = 1200;

/**
 * The narrows a boat of `boatKn` has to time, as gates for the router.
 *
 * ═══ THE THRESHOLD IS THE BOAT, NOT A CONSTANT ═══
 *
 * A station is a gate when its stream can beat the boat — that is the physical
 * fact that makes a pass something you wait for rather than something you push
 * through, and it is one sentence rather than a tuned number. It also adapts
 * honestly: a 5 kn sailing boat is gated by Active Pass at 6.7 kn, and a 12 kn
 * launch is not, because it can simply drive through.
 *
 * Measured against the shipped 2026 data, peak rate per station:
 *
 *   Okisollo 26.6 · Greene Point 22.6 · Whirlpool 22.6 · Yuculta 22.0
 *   Sechelt 16.7 · Seymour 15.6 · Arran 13.8 · Hole in the Wall 12.0
 *   Gillard 12.0 · Beazley 11.6 · Dent 10.5 · Tillicum 10.3 · Porlier 9.8
 *   Dodd 9.4 · Gabriola 8.0 · Camp Point 7.5 · Race Passage 7.3
 *   Active Pass 6.7 · Second Narrows 6.7 · Current Passage 6.5
 *   First Narrows 5.9 · Calamity Point 5.5 · Juan de Fuca East 3.7
 *   Bear Point 2.5 · Johnstone Strait 1.5 · Forward Bay 1.4 · Sunderland 0.8
 *
 * At 5 kn that gates everything down to Calamity Point and correctly leaves out
 * Juan de Fuca East, Johnstone Strait and the rest — which are open water where
 * a stream is a nuisance, not a door that shuts.
 *
 * `slacks` carries EVERY slack in the shipped year (~1410 per station), because
 * the whole reason gates keep working for a departure next week is that this
 * data does, unlike the model current field's rolling day or two.
 */
export function gatesFromStations(
  stations: readonly CurrentStation[],
  boatKn: number,
  radiusM = GATE_RADIUS_M
): Gate[] {
  const out: Gate[] = [];
  for (const st of stations) {
    let peakKt = 0;
    const slacks: number[] = [];
    for (const e of st.events) {
      if (e.kt > peakKt) peakKt = e.kt;
      if (e.kind === 'SLACK') slacks.push(e.t);
    }
    if (peakKt < boatKn || slacks.length === 0) continue;
    /* Already ascending out of `unpack`, which builds them from a running
     * delta — sorted anyway, because `openAt` binary-searches them and a single
     * out-of-order entry there is a silently wrong answer rather than a crash. */
    slacks.sort((a, b) => a - b);
    out.push({ code: st.code, name: st.name, lat: st.lat, lon: st.lon, radiusM, slacks });
  }
  return out;
}
